import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { findChildrenByMark } from '@atlaskit/editor-prosemirror/utils';

/**
 * Minimal structural view of an ADF entity. `parameters.nestedContent` holds raw ADF JSON rather
 * than ProseMirror nodes, so it cannot be walked with the model API.
 */
type AdfEntity = {
	attrs?: {
		[key: string]: unknown;
		parameters?: { nestedContent?: AdfEntity };
		text?: unknown;
	};
	content?: AdfEntity[];
	text?: string;
	type?: string;
};

/** Separates text from adjacent blocks so phrases cannot fuse across block boundaries. */
const BLOCK_SEPARATOR = ' ';

const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * Extracts text from raw ADF JSON.
 *
 * Needed because some extensions stash a whole ADF subtree in `attrs.parameters.nestedContent`
 * instead of in their node content — Confluence does this when it coerces nesting the schema
 * cannot represent (an expand more than two collapsible layers deep, say). Such a node is a
 * ProseMirror leaf, so the model walk cannot see the subtree even though it renders on the page
 * via a nested renderer. Recurses, because nestedContent routinely holds further nested content.
 */
const adfEntityText = (entity: AdfEntity | undefined): string => {
	if (!entity) {
		return '';
	}

	const parts: string[] = [];

	if (entity.type === 'text') {
		parts.push(asText(entity.text));
	}

	const nestedContent = entity.attrs?.parameters?.nestedContent;
	if (nestedContent) {
		// Prefer the real subtree over `attrs.text`, which is only a placeholder label for it.
		parts.push(adfEntityText(nestedContent));
	} else {
		parts.push(asText(entity.attrs?.text));
	}

	entity.content?.forEach((child) => parts.push(adfEntityText(child)));

	return parts.filter(Boolean).join(BLOCK_SEPARATOR);
};

/**
 * `textBetween` drops every non-text leaf unless the node spec declares `leafText`, and no ADF
 * node does. Leaves carry their visible text in attrs, so recover it here.
 */
const leafText = (leaf: PMNode): string => {
	const nestedContent = (leaf.attrs?.parameters as AdfEntity['attrs'] | undefined)?.nestedContent;
	if (nestedContent) {
		return adfEntityText(nestedContent as AdfEntity);
	}
	return asText(leaf.attrs?.text);
};

const hasInlineComment = (node: PMNode): boolean =>
	findChildrenByMark(node, node.type.schema.marks.annotation, true).some((annotation) =>
		annotation.node.marks.some((mark) => mark.attrs.annotationType === 'inlineComment'),
	);

/** `null` is a cached "no mirror for this node", as distinct from `undefined` for a cache miss. */
const searchTextCache = new WeakMap<PMNode, string | null>();

/**
 * Plain-text mirror of an expand's body, rendered in its place while collapsed so browser
 * find-in-page can still reach the content.
 *
 * Cached on the node. ProseMirror nodes are immutable and `render-document` memoises
 * `nodeFromJSON`, so the same node instance is reused across renders — including across the
 * serializer rebuilds that invalidate the module-level `memoizeOne` caches.
 *
 * @returns the mirror text, or `undefined` if this expand must keep its body rendered.
 */
export const getExpandSearchText = (node: PMNode): string | undefined => {
	let cached = searchTextCache.get(node);

	if (cached === undefined) {
		cached = hasInlineComment(node)
			? // Withhold the mirror, which makes Expand keep rendering `children`. Comment
				// navigation walks the DOM to find the annotated node and scrolls it into view, so
				// the annotation has to exist as a real element — a flat text mirror carries no
				// annotation marks and cannot stand in for it. Without this, stepping through
				// comments with the arrow keys silently fails to reach any comment inside a
				// collapsed expand. Costs the lazy-load win for these expands, deliberately.
				null
			: node.textBetween(0, node.content.size, BLOCK_SEPARATOR, leafText);
		searchTextCache.set(node, cached);
	}

	return cached ?? undefined;
};
